// Editor de foto de perfil real — pedido 2026-08-30 (tela "Meu perfil" do
// app PWA) e 2026-09-03 ("botão de troca no visual web tem que ser igual ao
// perfil do app pwa"). Fonte única compartilhada entre PWA (MeuPerfilScreen
// em mobile-app.tsx) e web (UserProfileDialog) pra nunca divergir — mesmo
// motivo documentado em StreetViewPanorama/ALERT_TYPE_META. Emoji 🙂 é o
// fallback enquanto não existe foto nenhuma, nunca um ícone genérico
// inventado.
import { useRef } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { useUserAvatar, useUploadAvatar, useRemoveAvatar } from "@/hooks/queries";
import { resolveGpsDriveUrl, GpsDriveApiError } from "@/lib/api/gpsdrive-client";

export function AvatarEditor({
  traccarUserId,
  size = 112,
}: {
  traccarUserId: number | undefined;
  size?: number;
}) {
  const { data: avatarData } = useUserAvatar(traccarUserId);
  const avatarUrl = avatarData?.imageUrls
    ? resolveGpsDriveUrl(avatarData.imageUrls["256"] ?? avatarData.imageUrls.original)
    : undefined;
  const uploadAvatar = useUploadAvatar();
  const removeAvatar = useRemoveAvatar();
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = async (file: File | undefined) => {
    if (!file || !traccarUserId) return;
    try {
      await uploadAvatar.mutateAsync({ traccarUserId, file });
      toast.success("Foto atualizada");
    } catch (err) {
      toast.error("Não foi possível trocar a foto", {
        description:
          err instanceof GpsDriveApiError && err.status === 413
            ? "A imagem é muito grande. O tamanho máximo permitido é 5 MB."
            : undefined,
      });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!traccarUserId) return;
    try {
      await removeAvatar.mutateAsync(traccarUserId);
      toast.success("Foto removida");
    } catch {
      toast.error("Não foi possível remover a foto");
    }
  };

  const busy = uploadAvatar.isPending || removeAvatar.isPending;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className="grid h-full w-full place-items-center overflow-hidden rounded-full bg-sky-100 text-5xl shadow-md dark:bg-sky-900/40">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span>🙂</span>
        )}
      </div>
      {/* Overlay de carregamento durante upload/remoção — antes o único sinal
          era o botão de câmera ficar desabilitado (sem nenhum feedback
          visível), parecia travado (relatado 2026-08-31: "demora pra trocar
          e não aparece nada carregando"). */}
      {busy && (
        <div className="absolute inset-0 grid place-items-center rounded-full bg-black/40">
          <Loader2 className="h-7 w-7 animate-spin text-white" aria-label="Carregando" />
        </div>
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy || !traccarUserId}
        className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full bg-sky-600 text-white shadow-lg disabled:opacity-60"
        aria-label="Trocar foto"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
      </button>
      {avatarUrl && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={busy}
          className="absolute -bottom-1 -left-1 grid h-8 w-8 place-items-center rounded-full bg-rose-600 text-white shadow-lg disabled:opacity-60"
          aria-label="Remover foto"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0])}
      />
    </div>
  );
}
